import { Suspense, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './AppLayout.module.css';
import Sidebar from '../Sidebar/Sidebar';
import BottomNav from '../BottomNav/BottomNav';
import PageErrorBoundary from '../Errors/PageErrorBoundary/PageErrorBoundary';
import PageLoader from '../PageLoader/PageLoader';
import Navbar from '../Navbar/NavBar';
import GlobalMessages from '../GlobalMessages/GlobalMessages';

const AppLayout = ({ currentProject, currentUser, projects, pendingInvitations, dashboards, refreshDashboards, initUser, setUser, setCurrentProject, children }) => {
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const bottomNavRef = useRef(null);

  const handleMenuClick = useCallback(() => {
    setIsMobileSidebarOpen(prev => !prev);
  }, []);

  const handleCloseMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  // Outside-click to close mobile sidebar.
  // setTimeout(0) avoids the race condition where the opening click
  // immediately triggers the outside-click handler.
  useEffect(() => {
    if (!isMobileSidebarOpen) return;

    const handleClickOutside = (e) => {
      const inSidebar = sidebarRef.current && sidebarRef.current.contains(e.target);
      const inBottomNav = bottomNavRef.current && bottomNavRef.current.contains(e.target);
      if (!inSidebar && !inBottomNav) {
        setIsMobileSidebarOpen(false);
      }
    };

    const tid = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(tid);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileSidebarOpen]);



  const isLoggedIn = currentUser && Object.keys(currentUser).length > 0;
  const isCreateProjectRoute = location.pathname === '/create-project';

  if (!isLoggedIn) {
    return (
      <div className={styles.pageWrapper}>
        <Navbar
          currentUser={currentUser}
          initUser={initUser}
          setUser={setUser}
          currentProject={currentProject}
          projects={projects}
          pendingInvitations={pendingInvitations}
          onSwitchProject={setCurrentProject}
        />
        <div className={styles.contentArea}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <Navbar
        currentUser={currentUser}
        initUser={initUser}
        setUser={setUser}
        currentProject={currentProject}
        projects={projects}
        pendingInvitations={pendingInvitations}
        onSwitchProject={setCurrentProject}
        onMenuClick={handleMenuClick}
      />
      <div className={styles.mainLayout}>
        {!isCreateProjectRoute && (
          <div ref={sidebarRef} className={styles.sidebarWrapper}>
            <Sidebar
              currentProject={currentProject}
              dashboards={dashboards}
              refreshDashboards={refreshDashboards}
              isCollapsed={isSidebarCollapsed}
              setIsCollapsed={setIsSidebarCollapsed}
              isMobileOpen={isMobileSidebarOpen}
              onNavigate={handleCloseMobileSidebar}
            />
          </div>
        )}
        <div className={styles.contentArea}>
          {/* project-global-messages-trd.md Section 4.1 Requirement 3:
              a sibling to Suspense inside .contentArea (not a sibling to
              Sidebar/Navbar), so it lives in the same flex column as the
              routed page content and pushes it down in normal document
              flow. Logged-in branch only -- the container requires an
              active project context. */}
          <GlobalMessages currentProject={currentProject} />
          <Suspense fallback={<PageLoader />}>
            <PageErrorBoundary key={location.pathname}>
              {children}
            </PageErrorBoundary>
          </Suspense>
        </div>
      </div>
      {isMobileSidebarOpen && <div className={styles.mobileOverlay} />}
      <div ref={bottomNavRef}>
        <BottomNav onMenuClick={handleMenuClick} />
      </div>
    </div>
  );
};

export default AppLayout;
